import { FC, ForwardedRef, ReactNode } from 'react';
import styled from 'styled-components';
import { useScrollTop } from '@hooks/gHooks/useScroll';
import { PullToRefreshify } from 'react-pull-to-refreshify';
import useAppStore from '@/store/appStore';
import { IMessage } from '@/store/messageStore';
import { SpinComp } from '@/Icons';
import { useUpdateEffect } from 'ahooks';

interface IMessageContainerProps {
  children: ReactNode;
  getMoreMessage?: () => Promise<IMessage[] | undefined> | undefined;
}

const MessageContainerWrap = styled.div`
  padding: 25rem 32rem;
  flex: auto;
  overflow-y: auto;

  > div {
    &:not(:first-child) {
      margin-top: 40rem;
    }
  }

  // mobile
  .pull-to-refreshify__body {
    > div {
      &:not(:first-child) {
        margin-top: 80rem;
      }
    }
  }
`;

const MessageContainer = forwardRef(
  (props: IMessageContainerProps, ref: ForwardedRef<{ messageContainerRef: HTMLDivElement }>) => {
    const appStore = useAppStore();
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(1); // 标记是否正在加载更多

    // Scroll directly to the bottom when the device size changes
    useUpdateEffect(() => {
      setTimeout(() => {
        const target = messageContainerRef.current;
        target &&
          target.scrollTo({
            top: target.scrollHeight,
            behavior: 'smooth',
          });
      }, 199);
    }, [appStore.curDevice]);

    const prevScrollHeight = useRef(0);

    const mobInput = useRef<HTMLElement | null>();
    useEffect(() => {
      mobInput.current = document.getElementById('mobInput');
    }, []);

    useEffect(() => {
      const msgListBox = messageContainerRef.current!;
      const disFromBottom = msgListBox.scrollHeight - prevScrollHeight.current;
      msgListBox.scrollTop = disFromBottom;
    }, [isLoadingMore]);

    useScrollTop(
      async () => {
        if (appStore.curDevice !== 'phone') {
          const msgListBox = messageContainerRef.current!;
          prevScrollHeight.current = msgListBox.scrollHeight; // 记录滚动条位置
          const messageList = await props.getMoreMessage?.();
          setIsLoadingMore(messageList?.length || 0);
        }
      },
      0,
      100,
      '#messageContainer'
    );

    useImperativeHandle<{ messageContainerRef: HTMLDivElement }, any>(ref, () => ({
      messageContainerRef: messageContainerRef.current,
    }));

    const [refreshing, setRefreshing] = useState(false);

    if (appStore.curDevice !== 'phone') {
      return (
        <MessageContainerWrap id="messageContainer" ref={messageContainerRef} className="py-20">
          {props.children}
        </MessageContainerWrap>
      );
    }

    return (
      <MessageContainerWrap
        onTouchStart={() => {
          mobInput.current?.blur();
        }}
        id="messageContainer"
        ref={messageContainerRef}
        className="py-20"
      >
        <PullToRefreshify
          refreshing={refreshing}
          onRefresh={async () => {
            setRefreshing(true);
            const msgListBox = messageContainerRef.current!;
            prevScrollHeight.current = msgListBox.scrollHeight; // 记录滚动条位置
            const messageList = await props.getMoreMessage?.()?.finally(() => {
              setRefreshing(false);
            });
            setIsLoadingMore(messageList?.length || 0);
          }}
          renderText={() => <SpinComp className="w-48" />}
        >
          {props.children}
        </PullToRefreshify>
      </MessageContainerWrap>
    );
  }
);

export default MessageContainer;
